import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Badge, Card, CardBody, CardHeader, Icon } from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-xs text-[var(--ad-text-muted)]">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-[var(--ad-text-muted)]">{hint}</p>}
      </CardBody>
    </Card>
  )
}

/**
 * Sihirbaz adımı → ekran. Adımların kendisi sunucudan geliyor ve GERÇEK
 * veriden türetiliyor (`/onboarding`); burada yalnızca "nereye gidilir".
 * Sunucu yeni bir adım eklerse bağlantısız ama görünür kalır — gizlenmez.
 */
const ADIM_YOLU: Record<string, string> = {
  tenant_profile: '/ayarlar/dernek',
  members: '/uyeler',
  fee_schedule: '/aidat',
  payment: '/odeme-ayarlari',
  team: '/ayarlar/kullanicilar',
  domain: '/ayarlar/alan-adlari',
}

/** Kurulum sihirbazı (YES-174). Tamamlanınca kart kaybolur. */
function KurulumSihirbazi() {
  const slug = useTenantSlug()
  const { data } = useQuery({
    queryKey: ['onboarding', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/onboarding')
      return data
    },
  })

  if (!data || data.complete) return null
  const zorunlu = data.steps.filter((adim) => adim.required)
  const biten = zorunlu.filter((adim) => adim.done).length

  return (
    <Card>
      <CardHeader
        title="Kurulum"
        description={`Zorunlu ${zorunlu.length} adımın ${biten}'i tamam. Bitince bu kart kaybolur.`}
        action={
          data.trial_ends_at ? (
            <Badge tone="info">
              Deneme bitişi {new Date(data.trial_ends_at).toLocaleDateString('tr-TR')}
            </Badge>
          ) : undefined
        }
      />
      <CardBody>
        <ol className="flex flex-col gap-1">
          {data.steps.map((adim) => {
            const yol = ADIM_YOLU[adim.key]
            const icerik = (
              <>
                <span
                  className={adim.done ? 'text-[var(--ad-success)]' : 'text-[var(--ad-text-muted)]'}
                  aria-hidden
                >
                  {adim.done ? '●' : '○'}
                </span>
                <span className="flex flex-col">
                  <span
                    className={
                      adim.done ? 'text-sm line-through opacity-70' : 'text-sm font-medium'
                    }
                  >
                    {adim.title}
                    {!adim.required && (
                      <span className="ml-2 text-xs font-normal text-[var(--ad-text-muted)]">
                        isteğe bağlı
                      </span>
                    )}
                  </span>
                  {!adim.done && (
                    <span className="text-xs text-[var(--ad-text-muted)]">{adim.description}</span>
                  )}
                </span>
                {!adim.done && yol && (
                  <Icon
                    name="chevrons-left-right"
                    size={16}
                    primaryColor="var(--ad-text-muted)"
                    className="ml-auto"
                    aria-hidden
                  />
                )}
              </>
            )
            const sinif =
              'flex items-start gap-3 rounded-[var(--radius-control)] px-2 py-2 ' +
              (adim.done || !yol ? '' : 'hover:bg-[var(--ad-surface-muted)]')
            return (
              <li key={adim.key}>
                {!adim.done && yol ? (
                  <Link to={yol} className={sinif}>
                    {icerik}
                  </Link>
                ) : (
                  <div className={sinif}>{icerik}</div>
                )}
              </li>
            )
          })}
        </ol>
      </CardBody>
    </Card>
  )
}

/** Özet ekranı: planın limitleri ve o ayki kullanım (YES-74). */
export function Ozet() {
  const slug = useTenantSlug()
  const { data } = useQuery({
    queryKey: ['subscription', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/subscription')
      return data
    },
  })

  const limits = data?.subscription.limits ?? {}
  const usage = data?.usage ?? {}
  const format = (key: string) => {
    const used = usage[key] ?? 0
    const allowed = limits[key]
    if (allowed === undefined) return used.toLocaleString('tr')
    // -1 sınırsız demek (YES-74).
    return allowed === -1
      ? `${used.toLocaleString('tr')} / sınırsız`
      : `${used.toLocaleString('tr')} / ${allowed.toLocaleString('tr')}`
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Özet</h1>
      <KurulumSihirbazi />
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Aktif üye" value={format('max_members')} hint="Plan limitine göre" />
        <Stat label="Bu ay e-posta" value={format('monthly_emails')} />
        <Stat label="Bu ay SMS" value={format('monthly_sms')} />
      </div>

      <Card>
        <CardHeader title="Plandaki özellikler" description="Menü bu listeye göre şekilleniyor." />
        <CardBody>
          <div className="flex flex-wrap gap-1.5">
            {(data?.subscription.features ?? []).map((feature) => (
              <span
                key={feature}
                className="rounded-full bg-[var(--ad-surface-muted)] px-2 py-0.5 text-xs"
              >
                {feature}
              </span>
            ))}
            {(data?.subscription.features?.length ?? 0) === 0 && (
              <p className="text-sm text-[var(--ad-text-muted)]">
                Bu derneğe henüz plan atanmamış; hiçbir özellik açık değil.
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
